import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Image, X, Check, Loader2, Link as LinkIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) return `${BASE_URL}${url}`;
  return url;
}

export { resolveUrl };

export default function ImagePicker({ value, onChange, label, multiple = false }) {
  const { getAuthHeader } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [tab, setTab] = useState('gallery');
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/gallery`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setGallery(data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
    setLoading(false);
  };

  const openPicker = () => {
    setIsOpen(true);
    fetchGallery();
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);

    const formData = new FormData();
    if (files.length === 1) {
      formData.append('file', files[0]);
      try {
        const response = await fetch(`${API}/admin/upload`, {
          method: 'POST',
          headers: { 'Authorization': getAuthHeader() },
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          toast.success('Photo uploadée');
          if (multiple) {
            onChange([...(value || []), data.url]);
          } else {
            onChange(data.url);
          }
          fetchGallery();
        } else {
          const err = await response.json();
          toast.error(err.detail || 'Erreur upload');
        }
      } catch (error) {
        toast.error('Erreur de connexion');
      }
    } else {
      for (const file of files) {
        formData.append('files', file);
      }
      try {
        const response = await fetch(`${API}/admin/upload-multiple`, {
          method: 'POST',
          headers: { 'Authorization': getAuthHeader() },
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          toast.success(`${data.length} photo(s) uploadée(s)`);
          if (multiple) {
            onChange([...(value || []), ...data.map(d => d.url)]);
          } else if (data.length > 0) {
            onChange(data[0].url);
          }
          fetchGallery();
        }
      } catch (error) {
        toast.error('Erreur de connexion');
      }
    }
    setUploading(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length) handleUpload(files);
  }, [value, multiple]);

  const selectFromGallery = (url) => {
    if (multiple) {
      const current = value || [];
      if (current.includes(url)) {
        onChange(current.filter(u => u !== url));
      } else {
        onChange([...current, url]);
      }
    } else {
      onChange(url);
      setIsOpen(false);
    }
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    if (multiple) {
      onChange([...(value || []), urlInput.trim()]);
    } else {
      onChange(urlInput.trim());
    }
    setUrlInput('');
    setIsOpen(false);
  };

  const removeImage = (url) => {
    if (multiple) {
      onChange((value || []).filter(u => u !== url));
    } else {
      onChange('');
    }
  };

  const currentValues = multiple ? (value || []) : (value ? [value] : []);

  return (
    <div>
      {label && <p className="font-body font-semibold text-forest text-sm mb-1">{label}</p>}

      {/* Preview */}
      {currentValues.length > 0 ? (
        <div className={`flex flex-wrap gap-2 mb-2 ${multiple ? 'grid grid-cols-4' : ''}`}>
          {currentValues.map((url, idx) => (
            <div key={idx} className="relative group">
              <img
                src={resolveUrl(url)}
                alt={`Photo ${idx + 1}`}
                className={`rounded-lg object-cover ${multiple ? 'w-full h-20' : 'w-full h-36'}`}
              />
              <button
                type="button"
                onClick={() => removeImage(url)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
              {multiple && idx === 0 && (
                <span className="absolute bottom-1 left-1 bg-forest/80 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  Principale
                </span>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <Button
        type="button"
        variant="outline"
        onClick={openPicker}
        className="w-full border-dashed border-2 py-6 hover:bg-forest/5"
        data-testid="image-picker-btn"
      >
        <Upload className="w-4 h-4 mr-2" />
        {currentValues.length > 0
          ? (multiple ? 'Ajouter des photos' : 'Changer la photo')
          : 'Choisir une photo'}
      </Button>

      {/* Picker Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-cream">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">
              {multiple ? 'Sélectionner des photos' : 'Sélectionner une photo'}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 bg-sand/30 rounded-lg p-1 mb-4">
            <button
              type="button"
              onClick={() => setTab('gallery')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-body font-medium transition-colors ${
                tab === 'gallery' ? 'bg-white text-forest shadow-sm' : 'text-forest/60 hover:text-forest'
              }`}
            >
              <Image className="w-4 h-4 inline mr-1.5" />
              Galerie
            </button>
            <button
              type="button"
              onClick={() => setTab('upload')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-body font-medium transition-colors ${
                tab === 'upload' ? 'bg-white text-forest shadow-sm' : 'text-forest/60 hover:text-forest'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-1.5" />
              Uploader
            </button>
            <button
              type="button"
              onClick={() => setTab('url')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-body font-medium transition-colors ${
                tab === 'url' ? 'bg-white text-forest shadow-sm' : 'text-forest/60 hover:text-forest'
              }`}
            >
              <LinkIcon className="w-4 h-4 inline mr-1.5" />
              URL
            </button>
          </div>

          {/* Gallery Tab */}
          {tab === 'gallery' && (
            <div className="overflow-y-auto max-h-[50vh]">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-forest/40" />
                </div>
              ) : gallery.length === 0 ? (
                <div className="text-center py-12 text-forest/50">
                  <Image className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="font-body text-sm">Aucune photo dans la galerie</p>
                  <button type="button" onClick={() => setTab('upload')} className="text-autumn text-sm mt-1 hover:underline">
                    Uploader des photos
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {gallery.map((img) => {
                    const isSelected = currentValues.includes(img.url);
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => selectFromGallery(img.url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                          isSelected ? 'border-autumn ring-2 ring-autumn/30' : 'border-transparent'
                        }`}
                      >
                        <img src={resolveUrl(img.thumb_url || img.url)} alt={img.filename} className="w-full h-full object-cover" />
                        {isSelected && (
                          <div className="absolute inset-0 bg-autumn/20 flex items-center justify-center">
                            <Check className="w-6 h-6 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {tab === 'upload' && (
            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
                dragOver ? 'border-autumn bg-autumn/5' : 'border-forest/20'
              }`}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 animate-spin text-autumn" />
                  <p className="font-body text-forest/70">Upload en cours...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-forest/30 mx-auto mb-3" />
                  <p className="font-body text-forest/70 mb-1">
                    Glissez-déposez vos photos ici
                  </p>
                  <p className="font-body text-sm text-forest/40 mb-4">
                    JPG, PNG, WebP - Max 10MB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Parcourir les fichiers
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUpload(Array.from(e.target.files))}
                  />
                </>
              )}
            </div>
          )}

          {/* URL Tab */}
          {tab === 'url' && (
            <div className="space-y-4">
              <p className="font-body text-sm text-forest/60">
                Collez l'URL d'une image externe
              </p>
              <div className="flex gap-2">
                <Input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrl())}
                />
                <Button type="button" onClick={addUrl} className="bg-forest hover:bg-forest-dark shrink-0">
                  Ajouter
                </Button>
              </div>
              {urlInput && (
                <img src={urlInput} alt="Preview" className="h-32 rounded-lg object-cover" onError={(e) => e.target.style.display='none'} />
              )}
            </div>
          )}

          {multiple && (
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={() => setIsOpen(false)} className="bg-forest hover:bg-forest-dark">
                Valider la sélection ({currentValues.length} photo{currentValues.length > 1 ? 's' : ''})
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
